import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, Video, Loader2, AlertTriangle, Calendar, Clock, X, CalendarX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOOKAHEAD_DAYS = 21;

// Batch 6 — coach/admin view of funnel bookings (services/coach_booking).
// Coaches see only their own; admins see all (backend-enforced).
// Batch 8b — per-row reschedule/cancel actions for confirmed, upcoming bookings.

const STATUS_BADGE = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-stone-200 text-stone-500',
    rescheduled: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    no_show: 'bg-red-100 text-red-700',
};

// Viewer's local IANA tz — sent to the backend + used for all slot display.
const VIEWER_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

const ymd = (d) => d.toISOString().slice(0, 10);                   // UTC date window bound
const dayKey = (iso) => new Date(iso).toLocaleDateString('en-CA'); // local-tz YYYY-MM-DD bucket
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

// ---- Reschedule modal ----------------------------------------------------
function RescheduleModal({ booking, getAuthHeader, onClose, onDone }) {
    const [slotPhase, setSlotPhase] = useState('loading'); // loading | ready | noslots
    const [slotsByDay, setSlotsByDay] = useState({});
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Public slots endpoint — coach + org come from the booking row. No auth header needed.
    const loadSlots = useCallback(async () => {
        setSlotPhase('loading');
        const now = new Date();
        const to = new Date(now.getTime() + LOOKAHEAD_DAYS * 86400000);
        try {
            const res = await axios.get(`${API}/public/coach/${booking.coach_user_id}/slots`, {
                params: { org_id: booking.org_id, date_from: ymd(now), date_to: ymd(to) },
            });
            const data = res.data || {};
            if (data.enabled === false) { setSlotPhase('noslots'); return; }
            const flat = [];
            Object.values(data.slots_by_day || {}).forEach(arr => (arr || []).forEach(s => flat.push(s)));
            flat.sort((a, b) => String(a.start_utc).localeCompare(String(b.start_utc)));
            const grouped = {};
            flat.forEach(s => { const k = dayKey(s.start_utc); (grouped[k] = grouped[k] || []).push(s); });
            const dk = Object.keys(grouped).sort();
            if (!dk.length) { setSlotPhase('noslots'); return; }
            setSlotsByDay(grouped); setDays(dk);
            setSelectedDay(prev => (prev && grouped[prev]) ? prev : dk[0]);
            setSelected(null);
            setSlotPhase('ready');
        } catch { setSlotPhase('noslots'); }
    }, [booking.coach_user_id, booking.org_id]);

    useEffect(() => { loadSlots(); }, [loadSlots]);

    const confirm = async () => {
        if (!selected || submitting) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/coach-bookings/${booking.booking_id}/reschedule`,
                { start_utc: selected.start_utc, timezone: VIEWER_TZ },
                { headers: getAuthHeader() });
            toast.success('Rescheduled');
            onDone();
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error(err.response?.data?.detail || 'That slot is no longer available');
                setSelected(null);
                loadSlots(); // refresh availability
            } else {
                toast.error(err?.response?.data?.detail || 'Could not reschedule. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const slots = slotsByDay[selectedDay] || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 p-5 border-b border-stone-100">
                    <div>
                        <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Reschedule booking</h2>
                        <p className="text-sm text-stone-500 mt-0.5">{booking.contact_name || booking.guest_name || '—'}</p>
                        <div className="flex items-center gap-1.5 mt-2 text-sm text-stone-700">
                            <Calendar className="w-4 h-4 text-stone-400" /> {fmtDateTime(booking.start_utc)}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 text-stone-400 hover:text-stone-700 rounded-lg hover:bg-stone-100">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-stone-700" />
                        <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a new time</p>
                        <span className="text-[11px] text-stone-400 ml-auto">{VIEWER_TZ.replace(/_/g, ' ')}</span>
                    </div>

                    {slotPhase === 'loading' && (
                        <div className="flex items-center justify-center gap-2 text-stone-400 text-sm py-8">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading available times…
                        </div>
                    )}

                    {slotPhase === 'noslots' && (
                        <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 text-center">
                            <Calendar className="w-7 h-7 text-stone-300 mx-auto mb-2" />
                            <p className="text-sm text-stone-500">No times available right now.</p>
                        </div>
                    )}

                    {slotPhase === 'ready' && (
                        <>
                            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                                {days.map(d => {
                                    const active = d === selectedDay;
                                    const iso = (slotsByDay[d][0] || {}).start_utc;
                                    return (
                                        <button key={d} type="button" onClick={() => { setSelectedDay(d); setSelected(null); }}
                                            className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border transition ${active ? 'bg-stone-900 text-white border-transparent' : 'text-stone-600 border-stone-200 hover:border-stone-300'}`}>
                                            {dayLabel(iso)}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-3 gap-2 mt-3">
                                {slots.map(s => {
                                    const active = selected && selected.start_utc === s.start_utc;
                                    return (
                                        <button key={s.start_utc} type="button" onClick={() => setSelected(s)}
                                            className={`px-2 py-2 rounded-lg text-xs font-medium border transition ${active ? 'bg-stone-900 text-white border-transparent' : 'text-stone-700 border-stone-200 hover:border-stone-400'}`}>
                                            {timeLabel(s.start_utc)}
                                        </button>
                                    );
                                })}
                            </div>

                            <button type="button" onClick={confirm} disabled={!selected || submitting}
                                className="mt-4 w-full px-5 py-3 rounded-xl bg-stone-900 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                                {submitting
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Rescheduling…</>
                                    : selected
                                        ? <><Clock className="w-4 h-4" /> Move to {dayLabel(selected.start_utc)} · {timeLabel(selected.start_utc)}</>
                                        : <>Pick a slot above</>}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---- Cancel modal --------------------------------------------------------
function CancelModal({ booking, getAuthHeader, onClose, onDone }) {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const confirm = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/coach-bookings/${booking.booking_id}/cancel`,
                { reason }, { headers: getAuthHeader() });
            toast.success('Booking cancelled');
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not cancel. Please try again.');
            setSubmitting(false); // let them retry
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <CalendarX className="w-5 h-5 text-red-500" />
                        <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Cancel booking</h2>
                    </div>
                    <p className="text-sm text-stone-500">
                        {booking.contact_name || booking.guest_name || 'This booking'} · {fmtDateTime(booking.start_utc)}
                    </p>

                    <textarea
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Reason (optional)"
                        rows={3}
                        className="mt-4 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-400 resize-none" />

                    <div className="flex gap-2 mt-4">
                        <button type="button" onClick={onClose} disabled={submitting}
                            className="flex-1 px-4 py-2.5 rounded-xl text-stone-600 font-medium text-sm border border-stone-200 hover:bg-stone-50 transition disabled:opacity-60 flex items-center justify-center gap-1.5">
                            <X className="w-4 h-4" /> Keep it
                        </button>
                        <button type="button" onClick={confirm} disabled={submitting}
                            className="flex-1 px-4 py-2.5 rounded-xl text-white font-medium text-sm bg-red-600 hover:bg-red-700 transition disabled:opacity-60 flex items-center justify-center gap-1.5">
                            {submitting
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Cancelling…</>
                                : <>Cancel booking</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CoachBookings() {
    const { getAuthHeader, user } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState('upcoming');   // upcoming | past
    const [status, setStatus] = useState('');          // '' = all
    const [rescheduleBooking, setRescheduleBooking] = useState(null);
    const [cancelBooking, setCancelBooking] = useState(null);
    const isAdmin = user?.is_admin || user?.is_super_admin;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { scope };
            if (status) params.status = status;
            const res = await axios.get(`${API}/coach-bookings`, { headers: getAuthHeader(), params });
            setBookings(res.data || []);
        } catch { toast.error('Failed to load bookings'); }
        finally { setLoading(false); }
    }, [getAuthHeader, scope, status]);

    useEffect(() => { load(); }, [load]);

    const canModify = (b) => b.status === 'confirmed' && new Date(b.start_utc) > new Date();

    return (
        <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 mb-6">
                <CalendarCheck className="w-6 h-6 text-stone-700" />
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Bookings</h1>
                    <p className="text-sm text-stone-500">{isAdmin ? 'All coach meetings booked from your funnels.' : 'Your booked coaching meetings.'}</p>
                </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
                <div className="flex rounded-lg border border-stone-200 overflow-hidden">
                    {['upcoming', 'past'].map(s => (
                        <button key={s} onClick={() => setScope(s)}
                            className={`px-4 py-1.5 text-sm font-medium capitalize ${scope === s ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>{s}</button>
                    ))}
                </div>
                <select value={status} onChange={e => setStatus(e.target.value)}
                    className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white">
                    <option value="">All statuses</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                    <option value="no_show">No-show</option>
                </select>
                <button onClick={load} className="ml-auto text-sm text-stone-500 hover:text-stone-800">Refresh</button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center gap-2 text-stone-400 py-16"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
            ) : bookings.length === 0 ? (
                <div className="text-center py-16 text-stone-400">
                    <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                    <p className="text-sm">No {scope} bookings.</p>
                </div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">When</th>
                                <th className="text-left font-medium px-4 py-3">Lead</th>
                                {isAdmin && <th className="text-left font-medium px-4 py-3">Coach</th>}
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="text-left font-medium px-4 py-3">Meeting</th>
                                <th className="text-right font-medium px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {bookings.map(b => (
                                <tr key={b.booking_id} className="hover:bg-stone-50">
                                    <td className="px-4 py-3 text-stone-800 whitespace-nowrap">{fmtDateTime(b.start_utc)}</td>
                                    <td className="px-4 py-3">
                                        <div className="text-stone-800">{b.contact_name || b.guest_name || '—'}</div>
                                        {(b.contact_email || b.guest_email) && <div className="text-xs text-stone-400">{b.contact_email || b.guest_email}</div>}
                                    </td>
                                    {isAdmin && <td className="px-4 py-3 text-stone-600">{b.coach_name || '—'}</td>}
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[b.status] || 'bg-stone-100 text-stone-500'}`}>{b.status}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {b.meet_link ? (
                                            <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:underline">
                                                <Video className="w-4 h-4" /> Join
                                            </a>
                                        ) : b.calendar_error ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-amber-600" title={b.calendar_error}><AlertTriangle className="w-3.5 h-3.5" /> No calendar</span>
                                        ) : <span className="text-stone-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {canModify(b) ? (
                                            <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                                                <button type="button" onClick={() => setRescheduleBooking(b)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-stone-700 border border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition">
                                                    <Clock className="w-3.5 h-3.5" /> Reschedule
                                                </button>
                                                <button type="button" onClick={() => setCancelBooking(b)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 border border-stone-200 hover:border-red-300 hover:bg-red-50 transition">
                                                    <CalendarX className="w-3.5 h-3.5" /> Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-right text-stone-300">—</div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {rescheduleBooking && (
                <RescheduleModal
                    booking={rescheduleBooking}
                    getAuthHeader={getAuthHeader}
                    onClose={() => setRescheduleBooking(null)}
                    onDone={() => { setRescheduleBooking(null); load(); }} />
            )}

            {cancelBooking && (
                <CancelModal
                    booking={cancelBooking}
                    getAuthHeader={getAuthHeader}
                    onClose={() => setCancelBooking(null)}
                    onDone={() => { setCancelBooking(null); load(); }} />
            )}
        </div>
    );
}
